import React from 'react';
import { useTranslation } from 'react-i18next';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Skeleton } from '@shared/components/ui/shadcn/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useMarketingData } from '../../hooks';
import { MarketingFilters } from '../../models';

const CHART_COLORS = ['#6366F1', '#60A5FA', '#34D399', '#FBBF24', '#F87171'];

interface OrderVolumeChartProps {
  filters?: MarketingFilters;
}

export const OrderVolumeChart = ({ filters }: OrderVolumeChartProps = {}) => {
  const { t } = useTranslation('marketingModule');

  const { segments, isLoading } = useMarketingData({ filters });
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const { totalCompanies, chartData } = React.useMemo(() => {
    if (!segments || segments.length === 0) {
      return { totalCompanies: 0, chartData: [] };
    }

    const total = segments.reduce((sum, s) => sum + s.total_clients, 0);

    const data = [
      {
        name: '0 pedidos',
        value: segments.find((s) => s.key === 'zero')?.total_clients || 0,
        range: '0',
        color: CHART_COLORS[0],
      },
      {
        name: '1–500 pedidos',
        value: segments.find((s) => s.key === 's_1_500')?.total_clients || 0,
        range: '1–500',
        color: CHART_COLORS[1],
      },
      {
        name: '501–2.000 pedidos',
        value: segments.find((s) => s.key === 's_501_2000')?.total_clients || 0,
        range: '501–2.000',
        color: CHART_COLORS[2],
      },
      {
        name: '2.001–5.000 pedidos',
        value: segments.find((s) => s.key === 's_2001_5000')?.total_clients || 0,
        range: '2.001–5.000',
        color: CHART_COLORS[3],
      },
      {
        name: '≥ 5.001 pedidos',
        value: segments.find((s) => s.key === 's_gt_5001')?.total_clients || 0,
        range: '≥ 5.001',
        color: CHART_COLORS[4],
      },
    ]
      .filter((item) => item.value > 0)
      .map((item) => {
        const segment = segments.find((s) => s.total_clients === item.value);
        return { ...item, percentage: segment?.percentage || 0 };
      });

    return { totalCompanies: total, chartData: data };
  }, [segments]);

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { name: string; value: number; percentage: number; color: string } }>;
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3 z-50">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: data.color }} />
            <div>
              <p className="font-medium text-card-foreground text-sm">{data.name}</p>
              <p className="text-xs text-muted-foreground">
                {data.value.toLocaleString()} {t('orderVolume.chart.companies', 'empresas')} ({data.percentage}%)
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <SectionBox data-testid="marketing-order-volume" className="h-full">
      <div className="space-y-5">
        <h2 className="text-lg font-semibold text-title">{t('orderVolume.title', 'Volumen Total de Pedidos')}</h2>

        {isLoading ? (
          <div className="flex flex-col items-center space-y-6 py-4">
            <Skeleton className="h-64 w-64 rounded-full" />
            <div className="w-full space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={`chart-row-skeleton-${i}`} className="flex items-center gap-3">
                  <Skeleton className="h-3 w-3 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-12" />
                </div>
              ))}
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            {t('orderVolume.empty', 'No hay datos para mostrar')}
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4">
            <div className="w-full flex justify-center">
              <div className="relative w-52 h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      isAnimationActive
                      stroke="none"
                      onMouseEnter={(_, index) => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          stroke="none"
                          style={{
                            transform: hoveredIndex === index ? 'scale(1.04)' : 'scale(1)',
                            transformOrigin: 'center',
                            transition: 'transform 0.2s ease-in-out',
                          }}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} offset={20} wrapperStyle={{ pointerEvents: 'none' }} />
                  </PieChart>
                </ResponsiveContainer>

                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('orderVolume.chart.totalClients', 'Total Empresas')}
                  </span>
                  <span className="text-3xl font-bold text-foreground tabular-nums">
                    {totalCompanies.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="w-full overflow-x-auto rounded-lg border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-2.5 px-4 font-medium text-sm text-muted-foreground">
                      {t('orderVolume.headers.ordersPerMonth', 'Pedidos / Mes')}
                    </th>
                    <th className="text-center py-2.5 px-4 font-medium text-sm text-muted-foreground">
                      {t('orderVolume.headers.companies', 'Nº Empresas')}
                    </th>
                    <th className="text-right py-2.5 px-4 font-medium text-sm text-muted-foreground">
                      {t('orderVolume.headers.percentage', 'Porcentaje')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((row, index) => (
                    <tr
                      key={index}
                      className="border-b border-border/50 last:border-b-0 hover:bg-muted/30 transition-colors"
                    >
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: row.color }}
                          />
                          <span className="font-medium text-sm text-foreground">{row.range}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className="text-sm font-semibold tabular-nums">{row.value.toLocaleString()}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <span className="text-sm text-muted-foreground tabular-nums">{row.percentage}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SectionBox>
  );
};
